/**
 * Frontend mirror of platform guide knowledge for instant/local answers.
 * Keep aligned with mic-backend/src/services/mcpQuiz/platformGuide.knowledge.js
 */

const GUIDE_ENTRIES = [
  {
    key: "stats",
    routes: ["/stats"],
    title: "Stats",
    what: "A dashboard of quiz attempts, scores, and engagement for your authorized scope.",
    why: "See whether students are active and how assessments are performing without opening each quiz.",
    how: [
      "Open Stats from the sidebar.",
      "Review attempt charts, score bands, and activity heat where available.",
      "Use the professor switcher (if shown) to focus on one lecturer’s data.",
    ],
    next: "If a topic looks weak, open Quiz Heat Map or Results for detail.",
  },
  {
    key: "copilot",
    routes: ["/microlab-copilot"],
    title: "Microlab Copilot",
    what: "Upload lecture materials and generate a structured study pack with AI.",
    why: "Turns lecture files into reusable teaching assets faster than building everything by hand.",
    how: [
      "Open Microlab Copilot.",
      "Upload supported lecture material.",
      "Review credit use before chargeable steps.",
      "Open the study pack and review AI output before using it.",
    ],
    next: "Approved materials can support teaching and question workflows.",
  },
  {
    key: "my_library",
    routes: ["/my-library"],
    title: "My Library",
    what: "Your institution’s curated teaching library of slides, models, and references.",
    why: "Control what your university exposes as shared teaching content.",
    how: [
      "Open My Library.",
      "Add or reference titles from available catalogs.",
      "Adjust visibility and order as needed.",
    ],
    next: "Use Presentations or quizzes that rely on this curated content.",
  },
  {
    key: "community",
    routes: ["/community"],
    title: "Community",
    what: "A discussion space for teaching questions and comments.",
    why: "Supports shared Q&A beyond formal quizzes.",
    how: ["Open Community.", "Browse or post questions.", "Add comments where appropriate."],
    next: "For graded assessment, use Create Quiz.",
  },
  {
    key: "my_titles",
    routes: ["/quiz/titles"],
    title: "Course Catalog (My Titles)",
    what: "Organize courses into titles and topics used by private questions and quizzes.",
    why: "Clear structure makes banks and quizzes easier to build and reuse.",
    how: [
      "Open Course Catalog under Quiz.",
      "Add a title (section).",
      "Add topics under each title.",
      "Use those topics when creating questions or quiz rules.",
    ],
    next: "Add questions in Question Bank, then Create Quiz.",
  },
  {
    key: "question_bank",
    routes: ["/quiz/questions"],
    title: "Question Bank",
    what: "Manage private MCQs and browse Microlab histology, embryology, and anatomy banks.",
    why: "Reuse quality questions and keep your bank aligned with courses and difficulties.",
    how: [
      "Open Question Bank.",
      "Switch between private and Microlab banks.",
      "Filter by topic, title, difficulty, or catalogue.",
      "Add/edit private questions; View Microlab questions (read-only).",
    ],
    next: "Create a quiz that pulls from private and/or Microlab sources.",
  },
  {
    key: "create_quiz",
    routes: ["/quiz/create"],
    title: "Create Quiz",
    what: "Build a timed quiz from topic rules, then share a code or QR.",
    why: "Delivers structured assessments without rebuilding questions each time.",
    how: [
      "Open Create Quiz.",
      "Set title, time limit, and rules.",
      "Add topic rules (bank, topic/catalogue, difficulty, count).",
      "If there is a shortage, generate missing private questions, create manually, use Copilot, or explicitly use available counts.",
      "Save and share the access code or QR.",
    ],
    next: "Monitor Heat Map and Results after students submit.",
  },
  {
    key: "heatmap",
    routes: ["/quiz/heatmap"],
    title: "Quiz Heat Map",
    what: "Visual overview of stronger and weaker topics from quiz performance.",
    why: "Highlights where reteaching or more practice will help most.",
    how: [
      "Open Heat Map under Quiz.",
      "Review topic performance patterns.",
      "Follow up with materials or a targeted quiz.",
    ],
    next: "Create a focused quiz or open Results for individual attempts.",
  },
  {
    key: "results",
    routes: ["/quiz/results"],
    title: "Quiz Results",
    what: "Submitted attempts and scores for authorized classes.",
    why: "Track how students performed and who needs support.",
    how: [
      "Open Results under Quiz.",
      "Filter by quiz where available.",
      "Inspect scores you are authorized to see.",
    ],
    next: "Use Heat Map for topic patterns or Stats for trends.",
  },
  {
    key: "presentations",
    routes: ["/presentationTitles"],
    title: "Presentations",
    what: "Create and deliver classroom presentations with supported Microlab content.",
    why: "Supports live teaching with the same content ecosystem as quizzes.",
    how: [
      "Ask the AI to draft a presentation (topic + how many slides), review, then confirm.",
      "Or open Presentations and create/edit in the maker.",
      "Use maker or mobile view for delivery.",
    ],
    next: "Pair with quizzes for practice after class.",
  },
  {
    key: "professors",
    routes: ["/professors"],
    title: "Professors",
    what: "Invite and manage lecturers for your university.",
    why: "Controls who can teach and own private teaching resources.",
    how: [
      "Open Professors.",
      "Add or invite a professor.",
      "Edit or remove as permitted.",
      "Resend invitation or credentials when needed.",
    ],
    next: "New professors can build titles, questions, and quizzes.",
  },
  {
    key: "students",
    routes: ["/students"],
    title: "Students",
    what: "Invite and manage students for your university.",
    why: "Controls who can take quizzes and use institutional teaching flows.",
    how: [
      "Open Students.",
      "Add or invite students.",
      "Edit or remove as permitted.",
      "Resend invitation or credentials when needed.",
    ],
    next: "Share quiz codes/QR from Create Quiz.",
  },
  {
    key: "histology",
    routes: ["/titles"],
    title: "Histology catalog",
    what: "Browse histology titles and slides.",
    why: "Access Microlab histology content for teaching and Microlab-bank quizzes.",
    how: [
      "Open Histology.",
      "Browse titles and open slides.",
      "Use Microlab Question Bank → Histology when building quizzes.",
    ],
    next: "Create Quiz with Microlab histology or open Presentations.",
  },
  {
    key: "embryology",
    routes: ["/embryologyTitles"],
    title: "Embryology catalog",
    what: "Browse embryology teaching titles and related content.",
    why: "Supports embryology teaching and Microlab-bank quizzes.",
    how: [
      "Open Embryology.",
      "Browse titles and resources.",
      "Use Microlab Question Bank → Embryology when creating quizzes.",
    ],
    next: "Create Quiz or Presentations as needed.",
  },
  {
    key: "anatomy",
    routes: ["/anatomyTitles"],
    title: "Anatomy catalog",
    what: "Browse anatomy titles and use the full-body 3D explorer.",
    why: "Spatial anatomy teaching with quizzes that can focus relevant structures.",
    how: [
      "Open Anatomy for the title catalog.",
      "Use Anatomy Explorer for interactive 3D.",
      "In Question Bank → Microlab → Anatomy, View questions with the body model when matched.",
    ],
    next: "Create anatomy quizzes from Microlab or private banks.",
  },
];

const GOAL_MAP = [
  { match: /struggle|weak topic|heat\s*map|where.*(fail|weak)/i, key: "heatmap" },
  { match: /from (my )?lecture|study pack|upload.*(pdf|lecture)|copilot/i, key: "copilot" },
  { match: /take an exam|assign.*(quiz|exam)|create.*(quiz|exam)|quiz code|qr/i, key: "create_quiz" },
  { match: /3d|anatomy.*(class|present)|present.*anatomy|presentation/i, key: "presentations" },
  { match: /share.*slides|library|curate/i, key: "my_library" },
  { match: /how did.*(class|students)|performance|this month|results|marks|scores/i, key: "results" },
  { match: /add.*(lecturer|professor)|invite.*professor/i, key: "professors" },
  { match: /invite.*student|add.*student/i, key: "students" },
  { match: /more.*(hard|difficult|medium|easy).*question|question bank|generate.*question/i, key: "question_bank" },
  { match: /stats|engagement|attempts chart/i, key: "stats" },
  { match: /histology/i, key: "histology" },
  { match: /embryology/i, key: "embryology" },
  { match: /anatomy/i, key: "anatomy" },
];

function findByKey(key) {
  return GUIDE_ENTRIES.find((e) => e.key === key) || null;
}

function findByRoute(pathname) {
  const path = String(pathname || "");
  return (
    GUIDE_ENTRIES.find((e) =>
      (e.routes || []).some((r) => path === r || path.startsWith(`${r}/`) || path.startsWith(r))
    ) || null
  );
}

function findByQuery(query) {
  const q = String(query || "").trim();
  if (!q) return null;
  for (const goal of GOAL_MAP) {
    if (goal.match.test(q)) return findByKey(goal.key);
  }
  const lower = q.toLowerCase();
  return (
    GUIDE_ENTRIES.find((e) => `${e.title} ${e.what} ${e.key}`.toLowerCase().includes(lower)) || null
  );
}

export function isPlatformGuideIntent(text) {
  const t = String(text || "");
  if (/^guide:/i.test(t.trim())) return true;
  return /\b(what is|how (do|can|to)|why (use|is|does)|explain|help me (with|use)|where (is|do)|guide me|tell me about)\b/i.test(
    t
  );
}

export function getPlatformGuideReply(text, { pathname = "", sectionKey = "" } = {}) {
  const raw = String(text || "").trim();
  const guideKey = raw.match(/^guide:([a-z0-9_]+)$/i)?.[1];
  let entry = guideKey ? findByKey(guideKey) : null;
  if (!entry) entry = findByQuery(raw);
  if (!entry && sectionKey) entry = findByKey(sectionKey);
  if (!entry) entry = findByRoute(pathname);

  if (!entry) {
    return {
      reply:
        "I can explain Microlab features such as Stats, Copilot, My Library, Course Catalog, Question Bank, Create Quiz, Heat Map, Results, Presentations, Professors, Students, Histology, Embryology, and Anatomy. What would you like to learn?",
      options: [
        { id: "guide:create_quiz", label: "Create Quiz", prompt: "How do I create a quiz?" },
        { id: "guide:question_bank", label: "Question Bank", prompt: "What is the Question Bank?" },
        { id: "guide:copilot", label: "Microlab Copilot", prompt: "What is Microlab Copilot?" },
      ],
    };
  }

  const steps = (entry.how || []).map((s, i) => `${i + 1}. ${s}`).join("\n");
  const route = entry.routes?.[0];
  return {
    reply: [
      `**${entry.title}**`,
      "",
      `**What it is:** ${entry.what}`,
      "",
      `**Why it matters:** ${entry.why}`,
      "",
      "**How to use it:**",
      steps,
      entry.next ? `\n**What happens next:** ${entry.next}` : null,
      "",
      "Would you like me to open that page or help with the next step?",
    ]
      .filter(Boolean)
      .join("\n"),
    options: [
      route
        ? { id: `nav:${entry.key}`, label: `Open ${entry.title}`, prompt: `__navigate__:${route}` }
        : null,
      { id: "create-quiz", label: "Create a quiz", prompt: "Create a quiz" },
      { id: "guide:question_bank", label: "Question Bank help", prompt: "How do I use the Question Bank?" },
    ].filter(Boolean),
  };
}
